import { X_MAX_TWEET_LENGTH, countTweetLength, type MediaAttachment } from '@postpilot/core/domain';
import { CalendarClock, FileText, Send } from 'lucide-react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/primitives';
import { api, ApiError } from '@/lib/api';
import { useAccountsStore } from '@/stores/accounts-store';
import { AccountSelector } from './account-selector';
import { CharacterRing } from './character-ring';
import { MediaUploader } from './media-uploader';
import { PostPreview } from './post-preview';
import { SchedulePicker } from './schedule-picker';

/**
 * The composer.
 *
 * Three outcomes from one form — post now, schedule, save a draft. They share
 * every field, so they are one form with three submit buttons rather than three
 * screens; splitting them would mean re-selecting accounts if you changed your
 * mind about when to send.
 *
 * State is local `useState`, not React Hook Form. RHF earns its keep on forms
 * with many independent fields and per-field validation; here there are three
 * fields, they are all interdependent (the character count, the account list and
 * the schedule all gate the same submit), and the live preview needs to re-render
 * on every keystroke anyway. RHF's uncontrolled-input advantage is worth nothing
 * when you are re-rendering regardless.
 */
export function ComposerPage() {
  const navigate = useNavigate();

  const accounts = useAccountsStore((s) => s.accounts);
  const selectedIds = useAccountsStore((s) => s.selectedIds);
  const toggleSelected = useAccountsStore((s) => s.toggleSelected);
  const selectAll = useAccountsStore((s) => s.selectAll);
  const clearSelected = useAccountsStore((s) => s.clearSelected);
  const connect = useAccountsStore((s) => s.connect);

  const [content, setContent] = React.useState('');
  const [media, setMedia] = React.useState<MediaAttachment[]>([]);
  const [scheduledAt, setScheduledAt] = React.useState<string | null>(null);
  const [scheduling, setScheduling] = React.useState(false);

  const [submitting, setSubmitting] = React.useState<'now' | 'schedule' | 'draft' | null>(null);
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  const charCount = countTweetLength(content);
  const isOverLimit = charCount > X_MAX_TWEET_LENGTH;
  const hasContent = content.trim().length > 0 || media.length > 0;
  const hasAccounts = selectedIds.length > 0;

  const selectedAccounts = accounts.filter((a) => selectedIds.includes(a.id));

  /** Post-now and schedule need content AND accounts. A draft needs neither. */
  const canPublish = hasContent && hasAccounts && !isOverLimit && submitting === null;

  const submit = async (intent: 'now' | 'schedule' | 'draft') => {
    setErrors({});
    setSubmitting(intent);

    try {
      const post = await api.posts.create(
        intent === 'schedule'
          ? { intent, content, accountIds: selectedIds, media, scheduledAt: scheduledAt! }
          : intent === 'now'
            ? { intent, content, accountIds: selectedIds, media }
            : { intent, content, accountIds: selectedIds, media, scheduledAt },
      );

      if (intent === 'draft') {
        toast.success('Draft saved');
      } else if (intent === 'schedule') {
        toast.success('Post scheduled', {
          description: `Going out to ${selectedIds.length} account${selectedIds.length === 1 ? '' : 's'}.`,
          action: { label: 'View queue', onClick: () => navigate('/queue') },
        });
      } else {
        // Deliberately not "Posted!". The worker publishes within a second or
        // two, but it has NOT published yet at the moment this resolves —
        // claiming success here and then showing a failure in the queue 3
        // seconds later would be a lie. See the queue for the real outcome.
        toast.success('Sending now', {
          description: 'Track it in the queue — it usually lands within a few seconds.',
          action: { label: 'View queue', onClick: () => navigate('/queue') },
        });
      }

      // Reset for the next post, but keep the account selection — the common
      // next action is another post to the same accounts.
      setContent('');
      setMedia([]);
      setScheduledAt(null);
      setScheduling(false);

      if (intent !== 'draft') navigate('/queue');
      else navigate(`/queue?highlight=${post.id}`);
    } catch (error) {
      if (error instanceof ApiError) {
        // Wire per-field errors from the server's Zod failure back into the form.
        if (error.fields) setErrors(error.fields);
        toast.error('Could not save the post', { description: error.message });
      } else {
        toast.error('Something went wrong', { description: 'Please try again.' });
      }
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Compose</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Write once, publish to every account you pick.
        </p>
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        {/* Editor */}
        <Card>
          <CardContent className="space-y-5 p-5">
            <AccountSelector
              accounts={accounts}
              selectedIds={selectedIds}
              onToggle={toggleSelected}
              onSelectAll={selectAll}
              onClear={clearSelected}
              onConnect={() => void connect('/compose')}
              error={errors.accountIds}
            />

            <div className="space-y-2">
              <div className="relative">
                <Textarea
                  value={content}
                  onChange={(event) => setContent(event.target.value)}
                  placeholder="What’s happening?"
                  rows={7}
                  aria-invalid={isOverLimit}
                  aria-label="Post content"
                  className="resize-none pb-10 text-base leading-relaxed"
                />

                {/* Sits inside the textarea's padding, the way X does it — the
                    counter belongs to the text, not to the form. */}
                <div className="absolute bottom-2.5 right-3">
                  <CharacterRing count={charCount} />
                </div>
              </div>

              {errors.content && (
                <p className="text-destructive text-xs" role="alert">
                  {errors.content}
                </p>
              )}
            </div>

            <MediaUploader media={media} onChange={setMedia} />

            {scheduling && (
              <SchedulePicker
                value={scheduledAt}
                onChange={setScheduledAt}
                error={errors.scheduledAt}
              />
            )}

            <div className="flex flex-wrap items-center gap-2 border-t pt-4">
              {!scheduling ? (
                <>
                  <Button
                    onClick={() => void submit('now')}
                    disabled={!canPublish}
                    loading={submitting === 'now'}
                  >
                    <Send />
                    Post now
                  </Button>

                  <Button
                    variant="outline"
                    onClick={() => setScheduling(true)}
                    disabled={submitting !== null}
                  >
                    <CalendarClock />
                    Schedule
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    onClick={() => void submit('schedule')}
                    disabled={!canPublish || !scheduledAt}
                    loading={submitting === 'schedule'}
                  >
                    <CalendarClock />
                    Schedule post
                  </Button>

                  <Button
                    variant="ghost"
                    onClick={() => {
                      setScheduling(false);
                      setScheduledAt(null);
                    }}
                    disabled={submitting !== null}
                  >
                    Cancel
                  </Button>
                </>
              )}

              <Button
                variant="ghost"
                className="ml-auto"
                onClick={() => void submit('draft')}
                // A draft has no requirements beyond *something* to save.
                disabled={!hasContent || submitting !== null}
                loading={submitting === 'draft'}
              >
                <FileText />
                Save draft
              </Button>
            </div>

            {/* The two reasons a publish button is disabled, said out loud. A
                greyed-out button with no explanation is a dead end. */}
            {hasContent && !hasAccounts && (
              <p className="text-muted-foreground text-xs">Pick at least one account to post to.</p>
            )}
            {isOverLimit && (
              <p className="text-destructive text-xs">
                {charCount - X_MAX_TWEET_LENGTH} characters over the limit. Links always count as 23
                characters, emoji as 2.
              </p>
            )}
          </CardContent>
        </Card>

        {/* Preview */}
        <div className="lg:sticky lg:top-20 lg:self-start">
          <PostPreview content={content} media={media} accounts={selectedAccounts} />
        </div>
      </div>
    </div>
  );
}
